/**
 * 图片空间弹窗缩略图错误恢复门禁。
 * 每个 imageId 最多一次恢复尝试；再次失败则永久标记，避免
 * onError → resolveSignedDisplayUrl / setState → 重挂载 → onError 请求风暴。
 */
export function createLibraryImageLoadGuard() {
  const recoveryAttempted = new Set();
  const failed = new Set();

  const normalizeId = (imageId) => {
    const id = String(imageId ?? '').trim();
    return id || '';
  };

  return {
    /** 已永久失败：调用方应停止重试并展示占位 */
    isFailed(imageId) {
      const id = normalizeId(imageId);
      return Boolean(id && failed.has(id));
    },

    /**
     * 开始一次恢复。
     * @returns {{ id: string, proceed: boolean, markedFailed: boolean }}
     * - proceed=true：允许执行一次恢复逻辑
     * - proceed=false 且 markedFailed=true：二次错误，已记入 failed
     * - proceed=false 且 markedFailed=false：已失败或无效 id，无需再改状态
     */
    beginRecovery(imageId) {
      const id = normalizeId(imageId);
      if (!id) return { id: '', proceed: false, markedFailed: false };
      if (failed.has(id)) return { id, proceed: false, markedFailed: false };
      if (recoveryAttempted.has(id)) {
        failed.add(id);
        return { id, proceed: false, markedFailed: true };
      }
      recoveryAttempted.add(id);
      return { id, proceed: true, markedFailed: false };
    },

    markFailed(imageId) {
      const id = normalizeId(imageId);
      if (!id) return false;
      if (failed.has(id)) return false;
      failed.add(id);
      return true;
    },

    reset() {
      recoveryAttempted.clear();
      failed.clear();
    },

    snapshotFailed() {
      return new Set(failed);
    },
  };
}

/**
 * 恢复得到的地址是否值得写入 urlMap（避免同资源反复 setState 触发重挂载）。
 * 相对路径与浏览器展开后的绝对 URL（pathname+search 相同）视为同一资源。
 */
export function shouldApplyRecoveredImageUrl(currentSrc, recoveredSrc) {
  const current = String(currentSrc || '').trim();
  const next = String(recoveredSrc || '').trim();
  if (!next) return false;
  if (!current) return true;
  if (current === next) return false;
  // data/blob 相对 view 恒视为新源；其余仅当字符串不同才应用
  if (next.startsWith('data:') || next.startsWith('blob:')) return true;
  if (current.startsWith('data:') || current.startsWith('blob:')) return true;
  try {
    const base = typeof window !== 'undefined' && window.location?.href
      ? window.location.href
      : 'http://local.invalid/';
    const a = new URL(current, base);
    const b = new URL(next, base);
    if (a.pathname === b.pathname && a.search === b.search) return false;
  } catch {
    // ignore invalid URL
  }
  return current !== next;
}
